/**
 * To prevent circular dependencies, please refrain from importing from requestSelectors.ts and try to keep an object oriented format for your selectors
 */
import { createSelector } from 'reselect';
import {
  IAppState,
  IContactUsState,
  IRequestFeedbackState,
} from '../../../../models/reduxState';
import {
  contactStateSelector
} from '../../../app/selectors/contactUsSelector';
import { RequestFeedbackSchema, RequestFeedbackSubmitStatus, SurveyCheckBoxCategories } from '../../models/feedbackModels';
import { CommunicationRequestType, FreelanceRequest, RequestState } from '../../models/requestModels';

export const requestFeedbackStateSelector = createSelector(
  contactStateSelector,
  (r: IContactUsState) => r.RequestFeedbackState
);

export const isRequestFeedbackEnabledSelector = createSelector(
  requestFeedbackStateSelector,
  (t: IRequestFeedbackState) => t.isRequestFeedbackEnabled
);

export const feedbackCommentMaxLengthSelector = createSelector(
  requestFeedbackStateSelector,
  (t: IRequestFeedbackState) => t.requestFeedbackCommentMaxLength
);

const getFeedbackDetails = (state: IAppState, requestId: string) => {
  return requestFeedbackStateSelector(state).allRequestFeedback.find((r: RequestFeedbackSchema) => r.RequestId === requestId);
};

export const allFeedbackDetailsSelector = createSelector(getFeedbackDetails, (t: RequestFeedbackSchema) => t);

const getRating = (requestFeedback: RequestFeedbackSchema): number => {
  return requestFeedback ? requestFeedback.Rating : -1;
};
export const ratingSelector = createSelector(getFeedbackDetails, getRating);

const getRatingText = (requestFeedback: RequestFeedbackSchema): string => {
  return requestFeedback ? requestFeedback.RatingText : String();
};
export const ratingTextSelector = createSelector(getFeedbackDetails, getRatingText);

const getIsProcessing = (requestFeedback: RequestFeedbackSchema): boolean => {
  return requestFeedback ? requestFeedback.IsProcessing : false;
};
export const isProcessingSelector = createSelector(getFeedbackDetails, getIsProcessing);

const getSurveyCheckBoxes = (
  requestFeedback: RequestFeedbackSchema
): SurveyCheckBoxCategories => {
  return requestFeedback && requestFeedback.SurveyCheckBoxes;
};
export const surveyCheckBoxCategoriesSelector = createSelector(getFeedbackDetails, getSurveyCheckBoxes);

const getIssueReolvedChoiceGroupKey = (requestFeedback: RequestFeedbackSchema): string => {
  let selectedKey = '';
  const r = requestFeedback;
  if (!!r && r.IsFeedbackCategoriesVisible) {
    selectedKey = r.IssueResolved ? 'true' : 'false';
  }
  return selectedKey;
};
export const issueReolvedChoiceGroupKeySelector = createSelector(getFeedbackDetails, getIssueReolvedChoiceGroupKey);

const getFeedbackIssueResolved = (requestFeedback: RequestFeedbackSchema): boolean => {
  return requestFeedback ? requestFeedback.IssueResolved : false;
};
export const feedbackIssueResolvedSelector = createSelector(getFeedbackDetails, getFeedbackIssueResolved);

const getFeedbackComment = (requestFeedback: RequestFeedbackSchema): string => {
  return requestFeedback ? requestFeedback.Comment : String();
};
export const feedbackCommentSelector = createSelector(getFeedbackDetails, getFeedbackComment);

const getFeedbackSubmitStatus = (
  requestFeedback: RequestFeedbackSchema
): RequestFeedbackSubmitStatus => {
  return requestFeedback ? requestFeedback.RequestFeedbackSubmitStatus : RequestFeedbackSubmitStatus.None;
};
export const feedbackSubmitStatusSelector = createSelector(getFeedbackDetails, getFeedbackSubmitStatus);

export const getRequest = (
  state: IAppState,
  requestId: string
): FreelanceRequest => {
  if (requestId && state && state.ContactState.RequestHistoryState.allRequests) {
    const request = state.ContactState.RequestHistoryState.allRequests.find(
      (r: FreelanceRequest) =>
        r &&
        r.RequestId &&
        r.RequestId.toLowerCase() === requestId.toLowerCase()
    );
    return request || null;
  }
  return null;
};

export const feedbackRequestSelector = createSelector(getRequest, (t: FreelanceRequest) => t);

export const getMSSolveTicketNumber = (request: FreelanceRequest): string => {
  if (!!request && !!request.SecondaryTicketType && request.SecondaryTicketType === CommunicationRequestType.MSaaS) {
    return request.SecondaryTicketNumber;
  }

  return String();
};
export const MSSolveTicketNumberSelector = createSelector(feedbackRequestSelector, getMSSolveTicketNumber);

// Get request properties
export const getState = (request: FreelanceRequest): RequestState => {
  if (!!request) {
    return RequestState[(request.State as unknown) as keyof typeof RequestState];
  }

  return RequestState.Unknown;
};
export const stateSelector = createSelector([feedbackRequestSelector], getState);

// Chained selectors
export const getIsActive = (requestState: RequestState): boolean => {
  switch (requestState) {
    case RequestState.Acknowledged:
    case RequestState.Active:
    case RequestState.Delayed:
      return true;
  }
  return false;
};
export const isActiveSelector = createSelector([stateSelector], getIsActive);

// Feedback is not expeced for MSaaS ticket
// IsFeedbackClosed is also set to true from rave
export const getIsFeedbackClosed = (request: FreelanceRequest, msSolveTicketNumber: string): boolean => {
  if (!!request) {
    return (!!msSolveTicketNumber ? true : request.IsFeedbackClosed);
  }

  return false;
};
export const isFeedbackClosedSelector = createSelector([feedbackRequestSelector, MSSolveTicketNumberSelector], getIsFeedbackClosed);

export const getIsClosed = (requestState: RequestState, isFeedbackClosed: boolean): boolean => {
  switch (requestState) {
    case RequestState.Closed:
    case RequestState.Cancelled:
    case RequestState.Completed:
      return isFeedbackClosed;
  }

  return false;
};
export const isClosedSelector = createSelector([stateSelector, isFeedbackClosedSelector], getIsClosed);

const getInFeedbackState = (
  isActive: boolean,
  isFeedbackClosed: boolean,
  isClosed: boolean,
  feedbackStatus: RequestFeedbackSubmitStatus
): boolean => {
  return (
    (!isActive && !isFeedbackClosed && !isClosed) ||
    feedbackStatus === RequestFeedbackSubmitStatus.Success ||
    feedbackStatus === RequestFeedbackSubmitStatus.Error
  );
};
export const inFeedbackStateSelector = createSelector(
  [isActiveSelector, isFeedbackClosedSelector, isClosedSelector, feedbackSubmitStatusSelector],
  getInFeedbackState
);
